import { Component } from '@theme/component';

/**
 * Homepage featured product: switches variant, media and prices client-side.
 * The theme's product-form-component still owns the add-to-cart request.
 */
class ASDFeaturedProduct extends Component {
  #abort;
  /** @type {Array<{id: number, options: string[], available: boolean, media: number, price: number, compare: number, plans: Record<string, {price: number, compare: number, renew: number}>}>} */
  #variants = [];
  /** @type {Intl.NumberFormat | undefined} */
  #money;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    try {
      this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '{}').variants || [];
    } catch {
      this.#variants = [];
    }
    this.#money = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });
    this.addEventListener('change', (event) => this.#onChange(event), options);
    this.addEventListener('click', (event) => this.#onClick(event), options);
    this.#render(this.#currentVariant());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /** @param {number} cents */
  #format(cents) {
    return this.#money ? this.#money.format(cents / 100) : String(cents / 100);
  }

  #selectedOptions() {
    return [...this.querySelectorAll('.asd-fp__option')].map(
      (fieldset) => /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ?? ''
    );
  }

  #currentVariant() {
    const picked = this.#selectedOptions();
    if (!picked.length) return this.#variants[0];
    return this.#variants.find((v) => v.options.every((value, i) => value === picked[i])) ?? this.#variants[0];
  }

  #selectedPlanInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="selling_plan"]:checked'));
  }

  /** @param {Event} event */
  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'selling_plan') {
      this.#render(this.#currentVariant());
      return;
    }
    const fieldset = target.closest('.asd-fp__option');
    if (!fieldset) return;
    // If the exact combination does not exist, fall back to the first variant carrying the changed value.
    const index = Number(/** @type {HTMLElement} */ (fieldset).dataset.optionIndex);
    let variant = this.#currentVariant();
    if (variant.options[index] !== target.value) {
      variant = this.#variants.find((v) => v.options[index] === target.value) ?? variant;
      this.#syncOptionInputs(variant);
    }
    this.#render(variant);
  }

  /** @param {{options: string[]}} variant */
  #syncOptionInputs(variant) {
    this.querySelectorAll('.asd-fp__option').forEach((fieldset, i) => {
      for (const input of fieldset.querySelectorAll('input')) input.checked = input.value === variant.options[i];
    });
  }

  /** @param {Event} event */
  #onClick(event) {
    const thumb = event.target instanceof Element ? event.target.closest('[data-media-id]') : null;
    if (thumb instanceof HTMLElement) this.#showMedia(thumb.dataset.mediaId);
  }

  /** @param {string | undefined} id */
  #showMedia(id) {
    if (!id) return;
    for (const panel of this.querySelectorAll('[data-media-panel]')) {
      const hide = /** @type {HTMLElement} */ (panel).dataset.mediaPanel !== id;
      /** @type {HTMLElement} */ (panel).hidden = hide;
      if (hide) for (const video of panel.querySelectorAll('video')) video.pause();
    }
    for (const thumb of this.querySelectorAll('[data-media-id]')) {
      thumb.setAttribute('aria-pressed', String(/** @type {HTMLElement} */ (thumb).dataset.mediaId === id));
    }
  }

  /** @param {{id: number, options: string[], available: boolean, media: number, price: number, compare: number, plans: Record<string, {price: number, compare: number, renew: number}>}} variant */
  #render(variant) {
    if (!variant) return;
    const servings = Number(this.dataset.servings) || 1;

    const idInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (idInput) idInput.value = String(variant.id);

    this.querySelectorAll('.asd-fp__option').forEach((fieldset, i) => {
      const value = variant.options[i];
      const label = fieldset.querySelector('[data-option-value]');
      if (label) label.textContent = value;
      for (const note of fieldset.querySelectorAll('[data-option-note]')) {
        /** @type {HTMLElement} */ (note).hidden = /** @type {HTMLElement} */ (note).dataset.optionNote !== value;
      }
    });

    if (variant.media) this.#showMedia(String(variant.media));

    // Plan cards: price each from this variant's allocations; hide plans it does not offer.
    let firstVisible = null;
    for (const card of this.querySelectorAll('[data-plan-card]')) {
      const input = /** @type {HTMLInputElement} */ (card.querySelector('input[name="selling_plan"]'));
      const planId = input.dataset.planId || '';
      const data = planId ? variant.plans[planId] : { price: variant.price, compare: variant.compare, renew: variant.price };
      /** @type {HTMLElement} */ (card).hidden = !data;
      input.disabled = !data;
      if (!data) continue;
      firstVisible ??= input;
      const price = card.querySelector('[data-plan-price]');
      const compare = /** @type {HTMLElement | null} */ (card.querySelector('[data-plan-compare]'));
      const day = card.querySelector('[data-plan-day]');
      if (price) price.textContent = this.#format(data.price);
      if (compare) {
        compare.textContent = this.#format(data.compare);
        compare.hidden = data.compare <= data.price;
      }
      if (day) day.textContent = this.#format(data.price / servings);
      const pct = card.querySelector('[data-save-percent]');
      const base = variant.compare || variant.price;
      if (pct) pct.textContent = String(base > data.price ? Math.floor(((base - data.price) * 100) / base) : 0);
    }

    let planInput = this.#selectedPlanInput();
    if (!planInput || planInput.disabled) {
      if (firstVisible) firstVisible.checked = true;
      planInput = firstVisible;
    }
    const planId = planInput?.dataset.planId || '';
    const active = planId ? variant.plans[planId] : { price: variant.price, compare: variant.compare, renew: variant.price };

    const renews = /** @type {HTMLElement | null} */ (this.querySelector('[data-renews]'));
    if (renews) {
      renews.hidden = !planId || !active;
      const renewPrice = renews.querySelector('[data-renew-price]');
      if (renewPrice && active) renewPrice.textContent = this.#format(active.renew);
    }

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('button[name="add"]'));
    if (button) {
      button.dataset.label ??= button.innerHTML;
      button.disabled = !variant.available;
      if (variant.available) {
        button.innerHTML = button.dataset.label;
        const total = button.querySelector('[data-add-total]');
        if (total && active) total.textContent = this.#format(active.price);
      } else {
        button.textContent = this.dataset.soldOutText || 'Sold out';
      }
    }
  }
}

if (!customElements.get('asd-featured-product')) customElements.define('asd-featured-product', ASDFeaturedProduct);
